/**
 * =============================================================================
 * ActivityPhaseStepper
 * Versão: 1.0.0
 *
 * Stepper de fases de uma pipeline em formato de "setas" (chevron),
 * coloridas conforme `phase_color`. Usado no header do
 * ActivityDetailsModalPremium para navegar entre fases do negócio.
 *
 * Estados visuais:
 *  - selected: cor cheia da fase, número branco, leve elevação
 *  - current  : fase atual do deal — anel + avatar do responsável abaixo
 *  - past     : opacidade reduzida (já avançou)
 *  - future   : muted
 *  - disabled : sem permissão de visualização (cursor-not-allowed)
 *
 * Reuso: lê fases via `usePipelinePhaseStats` (mesma RPC usada no Kanban
 * e no DealDetailsModalPremium).
 * =============================================================================
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { PhaseStats } from '@/hooks/usePipelinePhaseStats';

interface ActivityPhaseStepperProps {
  phases: PhaseStats[];
  /** Fase atual do deal (recebe o avatar abaixo) */
  currentPhaseId: string | null;
  /** Fase selecionada para visualização (controla destaque + lista esquerda) */
  selectedPhaseId: string | null;
  onSelectPhase: (phaseId: string) => void;
  /** Verifica permissão de visualização granular por fase */
  canViewPhase?: (phaseId: string) => boolean;
  /** Avatar/nome do responsável para mostrar abaixo da fase atual */
  sellerAvatar?: string | null;
  sellerName?: string | null;
}

/**
 * Converte cor (#hex ou hsl()) em background com opacidade controlada.
 * Mantém compatibilidade com qualquer formato vindo do banco.
 */
function withOpacity(color: string | null | undefined, alpha: number): string | undefined {
  if (!color) return undefined;
  const c = color.trim();
  if (c.startsWith('#') && (c.length === 7 || c.length === 4)) {
    const hex = c.length === 4
      ? c.slice(1).split('').map((ch) => ch + ch).join('')
      : c.slice(1);
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  // Fallback: deixa cor original (CSS var/hsl) — opacidade aplicada via opacity-*
  return c;
}

export function ActivityPhaseStepper({
  phases,
  currentPhaseId,
  selectedPhaseId,
  onSelectPhase,
  canViewPhase,
  sellerAvatar,
  sellerName,
}: ActivityPhaseStepperProps) {
  if (!phases.length) return null;

  // Identifica posição da fase atual para definir past/future
  const currentIdx = phases.findIndex((p) => p.phase_id === currentPhaseId);

  return (
    <div className="w-full overflow-x-auto scrollbar-thin">
      <div className="flex items-stretch gap-1 px-3 py-3 min-w-max">
        {phases.map((p, idx) => {
          const isSelected = p.phase_id === selectedPhaseId;
          const isCurrent = p.phase_id === currentPhaseId;
          const isPast = currentIdx >= 0 && idx < currentIdx;
          const isFuture = currentIdx >= 0 && idx > currentIdx;
          const allowed = canViewPhase ? canViewPhase(p.phase_id) : true;

          // Background: cheio se selecionada; semi-transparente caso contrário
          const fillColor = p.phase_color ?? '#7c3aed';
          const bgColor = isSelected
            ? fillColor
            : withOpacity(fillColor, isPast ? 0.18 : isFuture ? 0.10 : 0.28);

          return (
            <Tooltip key={p.phase_id}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  disabled={!allowed}
                  onClick={() => allowed && onSelectPhase(p.phase_id)}
                  className={cn(
                    // Base
                    'group relative flex flex-col items-center justify-center gap-1',
                    'min-w-[110px] px-5 py-2.5 text-xs font-medium select-none transition-all',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    // Forma de seta usando clip-path (chevron)
                    "[clip-path:polygon(0_0,calc(100%-12px)_0,100%_50%,calc(100%-12px)_100%,0_100%,12px_50%)]",
                    // Estados
                    !allowed && 'cursor-not-allowed opacity-40',
                    allowed && !isSelected && 'hover:brightness-110',
                    isSelected && 'shadow-md text-white',
                    !isSelected && 'text-foreground/80',
                    isCurrent && !isSelected && 'ring-2 ring-primary/40',
                  )}
                  style={{ backgroundColor: bgColor }}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-label={`Fase ${p.phase_name}`}
                >
                  <span
                    className={cn(
                      'flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold',
                      isSelected ? 'bg-white/20 text-white' : 'bg-background/60 text-foreground',
                    )}
                  >
                    {idx + 1}
                  </span>
                  <span className="text-[11px] leading-tight max-w-[120px] truncate">
                    {p.phase_name}
                  </span>

                  {/* Avatar do responsável abaixo da fase atual (mesmo estilo da imagem) */}
                  {isCurrent && (sellerAvatar || sellerName) && (
                    <span className="absolute -bottom-3 left-1/2 -translate-x-1/2">
                      <SmartAvatar
                        src={sellerAvatar ?? undefined}
                        name={sellerName ?? '—'}
                        size="xs"
                        use3DFallback
                      />
                    </span>
                  )}
                </button>
              </TooltipTrigger>
              <TooltipContent>
                {allowed ? p.phase_name : `Sem permissão para visualizar "${p.phase_name}"`}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </div>
  );
}
